/**
 * External dependencies
 */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Box, Stack, Text } from '@towersystems/towersystems-design-system';
import { map } from 'lodash';

/**
 * Internal dependencies
 */
import Card from 'components/card';
import { searchResourceConnects } from 'state/resource-connects/actions';
import usePagination from 'modules/dashboard/pagination/use-pagination';
import ResourceListTable from './resource-list-table';
import ButtonGroup from 'components/button-group';
import Button from 'components/button';
import Search from 'components/search';
import DropdownSelect from 'components/dropdown-select';
import ResourceDataModal from 'blocks/resource-info-modal';
import SyncResourceConnectModal from 'blocks/sync-resource-connect-modal';
import ResourceMetaDataModal from 'blocks/resource-meta-data-modal';

/**
 * Style dependencies
 */
import './styles.scss';

const ResourcesList = ({ connectionId, connectionType, ...props }) => {
    
    const { currentItems, setCriteria, requestItems, isRequesting, footer } = usePagination(
        props.searchResourceConnects,
        {
            criteria: { connection: connectionId },
            orderBy: { lastAttemptAt: 'desc' },
        },
    );

    let options = null;

    const magentoOptions = [{
                                value: '',
                                label: 'All resource types',
                            },
                            {
                                value: 'product',
                                label: 'Product resources',
                            },
                            {
                                value: 'configurable_product',
                                label: 'Configurable product',
                            },
                            {
                                value: 'department',
                                label: 'Department resources',
                            },
                            {
                                value: 'category',
                                label: 'Category resources',
                            },
                            {
                                value: 'customer',
                                label: 'Customer resources',
                            },
                            {
                                value: 'attribute',
                                label: 'Attribute resources',
                            },
                            {
                                value: 'attribute_set',
                                label: 'Attribute sets',
                            },
                           ];
    const roamOptions = [{
                            value: '',
                            label: 'All resource types',
                        },
                        {
                            value: 'product',
                            label: 'Product resources',
                        },
                        {
                            value: 'department_v2',
                            label: 'Department V2 resources',
                        },
                        {
                            value: 'category_v2',
                            label: 'Category V2 resources',
                        },
                        {
                            value: 'customer',
                            label: 'Customer resources',
                        },
                        {
                            value: 'supplier',
                            label: 'Supplier resources',
                        },
                        {
                            value: 'stocktake',
                            label: 'Stocktake resources',
                        },
                        {
                            value: 'tax_group',
                            label: 'Tax group resources',
                        },
                        {
                            value: 'variant_group',
                            label: 'Variant group resources',
                        },
                        {
                            value: 'paytype',
                            label: 'Paytype resources',
                        },
                        {
                            value: 'product_tag',
                            label: 'Product tag resources',
                        },
                        {
                            value: 'attribute',
                            label: 'Attribute resources',
                        },
                       ];
    const shopifyGQOptions = [{
                                value: '',
                                label: 'All resource types',
                            },
                            {
                                value: 'product',
                                label: 'Product resources',
                            },
                            {
                                value: 'department',
                                label: 'Department resources',
                            },
                            {
                                value: 'category',
                                label: 'Category resources',
                            },
                            {
                                value: 'product_tag',
                                label: 'Product tag resources',
                            },
                            {
                                value: 'attribute',
                                label: 'Attribute resources',
                            },
                            {
                                value: 'kits',
                                label: 'Kits resources',
                            },
                           ];
    const otherOptions = [{
                                value: '',
                                label: 'All resource types',
                            },
                            {
                                value: 'product',
                                label: 'Product resources',
                            },
                            {
                                value: 'department',
                                label: 'Department resources',
                            },
                            {
                                value: 'category',
                                label: 'Category resources',
                            },
                            {
                                value: 'customer',
                                label: 'Customer resources',
                            },
                            {
                                value: 'product_tag',
                                label: 'Product tag resources',
                            },
                            {
                                value: 'attribute',
                                label: 'Attribute resources',
                            },
                           ];
    if (connectionType === "magento") {
        options = magentoOptions;
    } else if (connectionType === "roam") {
        options = roamOptions;
    } else if (connectionType === "shopify_graphql") {
        options = shopifyGQOptions;
    } else {
        options = otherOptions;
    }

    return (
        <React.Fragment>
            <Stack className='resources__list-wrapper' space='large'>
                <Card>
                    <Box display='flex' width='full' padding='large'>
                        <Box display='flex' width='full'>
                            <Search
                                placeholder='Search resources'
                                onKeyPress={(e) => {
                                    let criteria = setCriteria({ resource_data: e.target.value });
                                    requestItems({ criteria });
                                }}
                            />
                        </Box>
                        <Box display='flex' alignItems='center' paddingLeft='large'>
                            <ButtonGroup width='full'>
                                <DropdownSelect
                                    className='resources'
                                    selected=''
                                    onChange={(type) => {
                                        let criteria = setCriteria({ resource_type: type });
                                        requestItems({ criteria });
                                    }}
                                    buttonRender={({ handleClick, title, label, arrow }) => (
                                        <Button style={{ width: 180 }} onClick={handleClick}>
                                            <Box alignItems='center' justifyContent='center' display='flex'>
                                                <Text size='xsmall'>{title}</Text>
                                                {arrow}
                                            </Box>
                                        </Button>
                                    )}
                                    options={options}
                                />
                                <DropdownSelect
                                    selected=''
                                    onChange={(action) => {
                                        let criteria = setCriteria({ action_needed: action });
                                        requestItems({ criteria });
                                    }}
                                    buttonRender={({ handleClick, title, label, arrow }) => (
                                        <Button style={{ width: 120 }} onClick={handleClick}>
                                            <Box alignItems='center' justifyContent='center' display='flex'>
                                                <Text size='xsmall'>{title}</Text>
                                                {arrow}
                                            </Box>
                                        </Button>
                                    )}
                                    options={[
                                        {
                                            value: '',
                                            label: 'All actions',
                                        },
                                        {
                                            value: 'delete',
                                            label: 'Delete',
                                        },
                                        {
                                            value: 'create',
                                            label: 'Create',
                                        },
                                        {
                                            value: 'update',
                                            label: 'Update',
                                        },
                                    ]}
                                />
                            </ButtonGroup>
                        </Box>
                    </Box>
                    <ResourceListTable requestItems={requestItems} requesting={isRequesting} items={currentItems} />
                </Card>
                {footer}
            </Stack>
            <ResourceDataModal />
            <SyncResourceConnectModal onClosed={requestItems} />
            <ResourceMetaDataModal />
        </React.Fragment>
    );
};

export default connect((state) => ({}), {
    searchResourceConnects,
})(ResourcesList);